import { SDKAction } from '../types/actions';
import bridge from '../../bridge/bridge';
import { Context, SDKActionCallbacks, SdkApi } from './types';
import { createLocalHostApiFactory } from '../utils/local-host';
import { NotifyBarcodeScannedRequest } from '../device/actions';
import { VerifyCustomerRequest } from '../order/actions';

/**
 * Initializes the SDK.
 *
 * @param callbacks Callbacks for SDK actions.
 * @returns Context object for the POS.
 * @throws {RequestError}
 */
const initialize: (callbacks: SDKActionCallbacks) => Promise<Context> =
  createLocalHostApiFactory((callbacks: SDKActionCallbacks) => {
    // Web Payments SDK also establishes the window.Square namespace.
    // To avoid namespace collisions, we are careful to only set our properties.
    if (window.Square) {
      window.Square.bridge = bridge;
    } else {
      window.Square = { bridge };
    }

    bridge.addon.onAddonRequest = async (request, onResolve, onReject) => {
      let onAddonRequestCallback;
      switch (request.actionName) {
        case SDKAction.SDK_GET_ADDON_INFO: {
          onAddonRequestCallback = callbacks[SDKAction.SDK_GET_ADDON_INFO];
          break;
        }

        case SDKAction.DEVICE_NOTIFY_BARCODE_SCANNED: {
          onAddonRequestCallback = callbacks[
            SDKAction.DEVICE_NOTIFY_BARCODE_SCANNED
          ].bind(
            this,
            (request as NotifyBarcodeScannedRequest).value.scannedCode
          );
          break;
        }

        case SDKAction.DEVICE_GET_RECEIPTS: {
          onAddonRequestCallback = callbacks[SDKAction.DEVICE_GET_RECEIPTS];
          break;
        }

        case SDKAction.ORDER_VERIFY_CUSTOMER: {
          onAddonRequestCallback = callbacks[
            SDKAction.ORDER_VERIFY_CUSTOMER
          ]?.bind(
            this,
            (request as VerifyCustomerRequest).value.customerLookupAttributes
          );
          break;
        }

        default:
      }
      if (onAddonRequestCallback) {
        try {
          onResolve(await onAddonRequestCallback());
        } catch (error) {
          onReject(error as Error);
        }
      } else {
        onReject(new Error('action unhandled by the addon.'));
      }
    };

    const context = sessionStorage.getItem('context');

    return context
      ? JSON.parse(context)
      : {
          configurations: {
            environmentUrl: '',
            merchantId: '',
            storeCode: '',
          },
          device: {
            hasScanner: false,
          },
          employee: {},
        };
  });

function setContext(context: Context) {
  sessionStorage.setItem('context', JSON.stringify(context));
}

const sdk: SdkApi = {
  initialize,
};

export { sdk, setContext };
